import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Paper, Button, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AssignmentTurnedIn, Badge, DirectionsCar, Language, Translate } from "@mui/icons-material";
import { gateway } from "../gateway";
import { colors } from "./ui/colors";
import { LoaderComponent } from "./Loader";
import { SnackbarAlert } from "./common/SnackbarAlert";

const SelectorCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  borderRadius: "16px",
  border: `1px solid ${selected ? colors.orange.base : "#f1f5f9"}`,
  backgroundColor: selected ? "rgba(255, 140, 0, 0.05)" : "var(--fx-surface-base, #FFFFFF)",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",

  "& .icon": {
    color: selected ? colors.orange.base : "#94a3b8",
    fontSize: "22px",
    marginBottom: "6px",
  },

  "& .text": {
    color: selected ? "black" : "#475569",
    fontWeight: 600,
    fontSize: "0.75rem",
  },

  "&:hover": {
    border: "1px solid #FF8C00",
  },
}));

const getIcon = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes("license")) return <Badge className="icon" />;
  if (n.includes("vehicle") || n.includes("car")) return <DirectionsCar className="icon" />;
  if (n.includes("english")) return <Language className="icon" />;
  if (n.includes("spanish")) return <Translate className="icon" />;
  return <AssignmentTurnedIn className="icon" />;
};

const isExperience = (name: string) => name.toLowerCase().includes("year") || name.toLowerCase().includes("exp");

export const ConnectBusinessRequirementsBar = () => {
  const tokenLocal = sessionStorage.getItem("token");

  const [requirements, setRequirements] = useState<any[]>([]);
  const [myRequirements, setMyRequirements] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [changes, setChanges] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<any>("success");
  const [alertMessage, setAlertMessage] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, selfRes] = await Promise.all([
        gateway.get(`/workrequirements`),
        gateway.get(`/workrequirements/self`, {
          headers: { Authorization: `${tokenLocal}` },
        }),
      ]);

      setRequirements(reqRes.data.data.items);
      const savedIds = selfRes.data.data?.map((item: any) => (typeof item === "string" ? item : item._id || item.id)) || [];

      setMyRequirements(savedIds);
      setChanges(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await gateway.put(`/workrequirements/self`, { requirements: myRequirements }, { headers: { Authorization: `${tokenLocal}` } });
      setChanges(false);
      setAlertMessage("Requirements saved successfully!");
      setAlertSeverity("success");
      setOpenAlert(true);
    } catch (err) {
      console.error(err);
      setAlertMessage("Error saving requirements");
      setAlertSeverity("error");
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleReq = (id: string, expMode = false) => {
    setMyRequirements((prev) => {
      let next = [...prev];

      if (expMode) {
        const expIds = requirements.filter((r) => isExperience(r.name)).map((r) => r._id || r.id);
        next = next.filter((existingId) => !expIds.includes(existingId));
      }

      const isSelected = prev.includes(id);
      const result = isSelected ? next.filter((i) => i !== id) : [...next, id];

      setChanges(true);
      return result;
    });
  };

  const gridReqs = requirements.slice(0, 4);
  const listReqs = requirements.slice(4);
  const experienceReqs = useMemo(() => listReqs.filter((r) => isExperience(r.name)), [listReqs]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "900px",
          borderRadius: "24px",
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box sx={{ p: 3, pb: 0, borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
          <Typography variant="h6" fontWeight={800}>
            Additional Informations
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* GRID */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {gridReqs.map((req) => {
              const id = req._id || req.id;
              const selected = myRequirements.includes(id);

              return (
                <Grid item xs={6} key={id}>
                  <SelectorCard selected={selected} onClick={() => toggleReq(id)}>
                    {getIcon(req.name)}
                    <Typography className="text">{req.name}</Typography>
                  </SelectorCard>
                </Grid>
              );
            })}
          </Grid>

          {/* EXPERIENCE */}
          <Box sx={{ mb: 4, width: "100%" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 800,
                color: "var(--fx-surface-texttertiary, #94A3B8)",
                letterSpacing: "1px",
                mb: 1.5,
              }}
            >
              YEARS OF EXPERIENCE
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-evenly",
              }}
            >
              {experienceReqs.map((req) => {
                const id = req._id || req.id;
                const selected = myRequirements.includes(id);

                let label = req.name.toLowerCase();

                if (label.includes("less")) {
                  label = "0-1";
                } else {
                  label = label.replace(/[^0-9+-]/g, "");
                }

                return (
                  <Box
                    key={id}
                    onClick={() => toggleReq(id, true)}
                    sx={{
                      px: 4,
                      py: 1,
                      borderRadius: "10px",
                      border: selected ? `1.8px solid ${colors.orange.base}` : "1.5px solid var(--fx-surface-border, #E2E8F0)",
                      backgroundColor: selected ? "rgba(255,140,0,0.05)" : "var(--fx-surface-sunken, #F8FAFC)",
                      color: selected ? colors.orange.base : "#64748b",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.2s ease",

                      "&:hover": {
                        borderColor: colors.orange.base,
                      },
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* SAVE */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "end" }}>
            <Button
              variant="contained"
              disabled={!changes}
              onClick={handleSave}
              sx={{
                bgcolor: colors.orange[500],
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { bgcolor: "#ea580c", boxShadow: "none" },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Paper>

      <SnackbarAlert openState={openAlert} setOpenState={setOpenAlert} severity={alertSeverity} duration={4500} message={alertMessage} />
      <LoaderComponent open={loading} />
    </Box>
  );
};
